import React, { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenant } from '../../../contexts/TenantContext';
import { useCreatorDetail } from '../../../hooks/useCreatorDetail';
import Sidebar from '../../admin-dashboard/components/Sidebar';
import CreatorDetailHero from '../../../components/creators-v2/CreatorDetailHero';
import CreatorDetailActivity from '../../../components/creators-v2/CreatorDetailActivity';

/**
 * Manager V2 Team Detail — Détail d'un créateur dans le scope manager.
 * Route : /manager/team/:id + /manager/v2/team/:id.
 * Feature flag : `pages.creators.detail_manager`.
 *
 * Si le créateur n'appartient pas à l'équipe du manager (manager_id différent
 * de userProfile.id), le hook renverra null → page affiche notFound.
 */
const ManagerV2TeamDetail = () => {
  const { t } = useTranslation();
  const { userProfile } = useAuth();
  const { tenantName } = useTenant();
  const { id } = useParams();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const { creator, currentMonth, dailyRows, activity, loading, error, reload } = useCreatorDetail({
    creatorId: id,
    lookbackDays: 60,
  });

  // Defensive : ensure the creator belongs to this manager (RLS should also enforce)
  const accessDenied = !loading && creator && creator.manager_id !== userProfile?.id;

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} userEmail={userProfile?.email} />

      <main className="flex-1 min-w-0 overflow-x-hidden">
        <header className="border-b border-border bg-card sticky top-0 z-10">
          <div className="px-4 md:px-6 py-4 flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/manager/team')}
              className="shrink-0 text-muted-foreground hover:text-foreground text-sm"
              aria-label={t('common.back', 'Back')}
            >
              ←
            </button>
            <div className="min-w-0">
              <Link to="/manager/team" className="text-xs text-muted-foreground hover:text-foreground">
                {t('creatorsV2.managerTitle', 'My team')}
              </Link>
              <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground truncate">
                {creator?.display_name || creator?.tiktok_username || '—'}
              </h1>
              <p className="text-xs text-muted-foreground mt-0.5">{tenantName}</p>
            </div>
          </div>
        </header>

        <div className="p-4 md:p-6 space-y-4 md:space-y-6">
          {error && (
            <div className="rounded-2xl bg-error/5 border border-error/30 p-4 text-sm text-error flex items-center justify-between">
              <span>{String(error.message || error)}</span>
              <button type="button" onClick={reload} className="px-3 py-1.5 rounded-lg border border-error/40 hover:bg-error/10 text-xs font-medium">
                {t('common.retry', 'Retry')}
              </button>
            </div>
          )}

          {loading && !creator ? (
            <div className="rounded-2xl bg-card border border-border h-48 skeleton" />
          ) : !creator ? (
            <div className="rounded-2xl bg-card border border-border p-8 text-center text-sm text-muted-foreground">
              {t('creatorsV2.detail.notFound', 'Creator not found or not accessible to your tenant.')}
            </div>
          ) : accessDenied ? (
            <div className="rounded-2xl bg-warning/5 border border-warning/30 p-8 text-center text-sm text-warning">
              {t('creatorsV2.detail.accessDenied', 'This creator is not in your team.')}
            </div>
          ) : (
            <>
              <CreatorDetailHero creator={creator} currentMonth={currentMonth} role="manager" />
              <CreatorDetailActivity activity={activity} dailyRows={dailyRows} lookbackDays={60} loading={loading} />
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default ManagerV2TeamDetail;
